import styles from './operationItemForm.module.css'
import { useEffect, useMemo, useState } from "react"
import { Form, ModalId, useUIActions, FormField, Hint, FieldType, O, useUIState, FieldValue, InputGroup, option, Option, StaticValue, PriceField, PercentField, Icon, onManualChange } from "../../../../features/ui"
import { TableName } from "../../../../tables"
import { Lot, LotOption, Product, ProductSelectField, ProductType, useLotLoaders, useProductLoaders } from "../../../products"
import { Item } from "../../state/types"
import { useManagerState } from "../../../manager"
import { useUnitTypeLoaders } from "../../../stock"
import { usePricesHooks } from "../../hooks/pricesHooks"
import { isDefined, round } from '../../../../utils/typeUtils'
import { Discount } from '../../../common'
import { usePriceOutlineHooks } from '../../hooks/priceOutlineHooks'

type Props = {
    defaultData?: Partial<Item>
    createMode?: boolean
    customerTypeId?: string
    branchId?: string
    withLots?: boolean
    priceLabel?: string
    discountLabel?: string
    discountHint?: string
    defaultPrice: (productId: string) => number
    defaultDiscounts?: (productId: string) => Promise<Discount[]>
    filterProducts?: (product: Product) => boolean
    filterLots?: (lot: Lot) => boolean
    withSpecialDiscount?: (data: Partial<Item>) => boolean
    onSubmit: (data: Partial<Item>) => void
}

export const OperationItemForm = (props: Props) => {
    const { currentBranch } = useManagerState()
    const { defaultData, customerTypeId, branchId = currentBranch!.id, withLots, priceLabel, discountLabel, discountHint, filterProducts, filterLots, defaultPrice, defaultDiscounts, withSpecialDiscount, onSubmit } = props
    const createMode = isDefined(props.createMode) ? props.createMode : !defaultData
    
    const { openedModal } = useUIState()

    const pricesHooks = usePricesHooks()
    const { getItemPriceOutlineLive, getItemPriceOutline } = usePriceOutlineHooks()

    const [products, setProducts] = useState<Product[]>([])
    const [lots, setLots] = useState<Lot[]>([])
    const [data, setData] = useState<O<Partial<Item>>>(defaultData)
    const [specialDiscount, setSpecialDiscount] = useState<boolean>(!!defaultData?.specialDiscountYield)
    const priceOutline = useMemo(() => data?.productId ?
        (createMode ? getItemPriceOutlineLive(data, customerTypeId) : getItemPriceOutline(data! as Item)) :
        undefined
    , [data, customerTypeId])

    const [specialPrice, setSpecialPrice] = useState<O<number>>(priceOutline?.specialDiscountUnitSubtotal)
    const [focusProduct, setFocusProduct] = useState(false)

    const productId = useMemo(() => data?.productId, [data?.productId])
    const showSpecialDiscount = useMemo(() => !withSpecialDiscount || (data && withSpecialDiscount(data)), [withSpecialDiscount, data?.productId])

    const productLoaders = useProductLoaders()
    const lotLoaders = useLotLoaders()
    const unitTypeLoaders = useUnitTypeLoaders()

    const { closeModal } = useUIActions()

    const loadProducts = async () => {
        const { loadAllProducts } = productLoaders()
        const loadedProducts = await loadAllProducts()
        const activeProducts = loadedProducts.filter(product => product.id === data?.productId || !product.archived)
        return filterProducts ? activeProducts.filter(filterProducts) : loadedProducts
    }

    useEffect(() => {
        loadProducts().then(setProducts)
    }, [])

    const loadLots = async () => {
        const { loadProductLots } = lotLoaders()
        const loadedLots = productId ? await loadProductLots(productId) : []
        return filterLots ? loadedLots.filter(filterLots) : loadedLots
    }

    useEffect(() => {
        withLots && loadLots().then(setLots)
    }, [productId])

    useEffect(() => {
        setTimeout(() => setFocusProduct(openedModal === ModalId.OPERATION_ITEM_FORM), 500)
    }, [openedModal])

    const product = useMemo(() => products.find(product => product.id === productId), [products, productId])
    const unitType = useMemo(() => unitTypeLoaders().findUnitType(product?.unitTypeId), [product?.unitTypeId])
    const isBulk = useMemo(() => product?.type === ProductType.BULK, [product?.type])
    const productDiscountsPrice = useMemo(() => priceOutline?.productDiscountsUnitSubtotal || 0, [priceOutline?.productDiscountsUnitSubtotal])

    const onChangeProduct = async (value?: FieldValue) => {
        const newProductId = value as O<string>
        const newQuantity = defaultData?.quantity || 1
        const newData: Partial<Item> = {
            ...data,
            productId: newProductId,
            lotId: undefined,
            quantity: newQuantity,
            ...(newProductId && {
                price: defaultPrice(newProductId)
            }),
        }
        if (defaultDiscounts && newProductId) {
            newData.discounts = await defaultDiscounts(newProductId)
        }
        setData(newData)
        setSpecialDiscount(false)
    }

    const renderLotOption = async (
        option: Option,
        onClick?: (e: React.MouseEvent<HTMLElement>) => void
    ) => {
        const lot = lots.find(lot => lot.id === option.value)!
        return <LotOption
            product={product!}
            lot={lot}
            branchId={branchId}
            onClick={onClick}
        />
    }

    const onChangeLot = (value?: FieldValue) => setData({ ...data, lotId: value as O<string> })

    const onChangeQuantity = (value?: FieldValue) => setData({ ...data, quantity: value as O<number> })

    const onChangeSpecialDiscount = (value?: FieldValue) => {
        const newSpecialDiscount = !!value
        setSpecialDiscount(newSpecialDiscount)
        if (newSpecialDiscount) {
            setData({ ...data, specialDiscountYield: 5 })
            setSpecialPrice(pricesHooks.adjustment.apply(productDiscountsPrice, -5))
        } else {
            setData({ ...data, specialDiscountYield: undefined })
            setSpecialPrice(undefined)
        }
    }

    const onChangeSpecialYield = onManualChange((value?: FieldValue) => {
        const newYield = value as O<number>
        setData({
            ...data,
            specialDiscountYield: newYield,
        })
        if (newYield) {
            setSpecialPrice(pricesHooks.adjustment.apply(productDiscountsPrice, -newYield))
        }
    })

    const onChangeSpecialPrice = onManualChange((value?: FieldValue) => {
        const newPrice = value as O<number>
        setSpecialPrice(newPrice)
        if (newPrice) {
            setData({
                ...data,
                specialDiscountYield: -pricesHooks.adjustment.calculateYield(newPrice, productDiscountsPrice)
            })
        }
    })

    const fields: FormField[] = [
        {
            name: 'productId',
            type: FieldType.TABLE,
            table: TableName.PRODUCTS,
            label: 'Producto',
            loading: products.length === 0,
            render: () => <ProductSelectField
                productList={products}
                branchId={branchId}
                defaultValue={data?.productId}
                focus={focusProduct}
                onChange={onChangeProduct}
            />
        },
        {
            name: 'price',
            type: FieldType.PRICE,
            label: priceLabel || 'Precio de Lista',
            render: () => <StaticValue value={productDiscountsPrice} type={FieldType.PRICE} />
        },
        {
            name: 'quantity',
            type: FieldType.NUMBER,
            label: 'Cantidad',
            value: data?.quantity,
            min: 1,
            integer: !isBulk,
            onChange: onChangeQuantity,
            render: isBulk ? () => (
                <InputGroup
                    id="quantity"
                    value={data?.quantity}
                    postLabel={unitType?.abbreviation}
                    min={0.01}
                    onChange={onChangeQuantity}
                />
            ) : undefined
        }
    ]
    if (showSpecialDiscount) {
        if (specialDiscount) {
            const specialDiscountYieldField = {
                name: 'specialDiscountYield',
                type: FieldType.PRICE,
                label: ' ',
                render: () => (
                    <div className={styles.specialDiscount}>
                        <PercentField
                            id="specialYield"
                            value={data?.specialDiscountYield && round(data?.specialDiscountYield)}
                            onChange={onChangeSpecialYield}
                        />
                        <Icon icon="arrow-left-right" small />
                        <PriceField
                            id="specialPrice"
                            value={specialPrice}
                            min={1}
                            onChange={onChangeSpecialPrice}
                        />
                    </div>
                )
            }
            fields.splice(2, 0, specialDiscountYieldField)
        }
        const specialDiscountField = {
            name: 'specialDiscount',
            type: FieldType.BOOLEAN,
            label: discountLabel || 'Descuento Especial',
            value: specialDiscount,
            disabled: !data?.productId,
            hint: discountHint,
            onChange: onChangeSpecialDiscount
        }
        fields.splice(2, 0, specialDiscountField)
    }
    if (withLots && product?.loted) {
        const lotField = {
            key: `lot-id-${productId}`,
            name: 'lotId',
            type: FieldType.SELECT,
            label: 'Partida',
            defaultValue: data?.lotId,
            options: lots.map(lot => option(lot.id, lot.code)),
            placeholder: 'Seleccionar partida',
            renderOption: renderLotOption,
            onChange: onChangeLot
        }
        fields.splice(1, 0, lotField)
    }

    const handleSubmit = () => {
        onSubmit({
            ...data!,
            customerTypeYield: priceOutline!.customerTypeYield
        })
        closeModal(ModalId.OPERATION_ITEM_FORM)
    }

    const handleCancel = () => closeModal(ModalId.OPERATION_ITEM_FORM)

    return (
        <Form
            formId="operation-item-form"
            fields={fields}
            createMode={createMode}
            createLabel="Agregar"
            onSubmit={handleSubmit}
            onCancel={handleCancel}
        />
    )
}
